import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { 
  FiSave, 
  FiArrowLeft,
  FiUser,
  FiLink,
  FiTag,
  FiStar,
  FiEye,
  FiTrash2,
  FiPlus,
  FiImage,
  FiVideo
} from 'react-icons/fi'
import { useArtists, useArtist } from '../../hooks/useArtists'
import { ArtistFormData } from '../../types/artist'
import MediaPicker from './MediaPicker'
import VideoPicker from './VideoPicker'

interface ArtistFormProps {
  mode: 'add' | 'edit'
}

const ArtistForm: React.FC<ArtistFormProps> = ({ mode }) => {
  const navigate = useNavigate()
  const { id: artistId } = useParams<{ id: string }>()
  const { createArtist, updateArtist } = useArtists()
  const { artist, loading: artistLoading } = useArtist(mode === 'edit' ? artistId || '' : '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showMediaPicker, setShowMediaPicker] = useState(false)
  const [showVideoPicker, setShowVideoPicker] = useState(false)

  const { register, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm<ArtistFormData>({
    defaultValues: {
      name: '',
      genre: '',
      description: '',
      image: '',
      videoUrl: '',
      videoThumbnail: '',
      stats: {
        streams: '',
        followers: '',
        shows: ''
      },
      socialLinks: {
        instagram: '',
        twitter: '',
        spotify: '',
        youtube: '',
        website: '',
        facebook: ''
      },
      featured: false,
      active: true,
      gradient: 'from-blue-500/20 to-purple-500/20',
      tags: ['']
    }
  })

  const [tags, setTags] = useState<string[]>([''])

  // Load artist data for edit mode
  useEffect(() => {
    if (mode === 'edit' && artist) {
      reset({
        name: artist.name,
        genre: artist.genre,
        description: artist.description,
        image: artist.image,
        videoUrl: artist.videoUrl || '',
        videoThumbnail: artist.videoThumbnail || '',
        stats: artist.stats,
        socialLinks: artist.socialLinks,
        featured: artist.featured,
        active: artist.active,
        gradient: artist.gradient,
        tags: []
      })
      setTags(artist.tags || [''])
    }
  }, [mode, artist, reset])

  const onSubmit = async (data: ArtistFormData) => {
    setIsSubmitting(true)

    try {
      // Filter out empty tags
      const processedData = {
        ...data,
        tags: tags.filter(tag => tag.trim() !== '')
      }

      if (mode === 'add') {
        const newArtistId = await createArtist(processedData)
        if (newArtistId) {
          navigate('/admin/artists')
        }
      } else {
        const success = await updateArtist(artistId!, processedData)
        if (success) {
          navigate('/admin/artists')
        }
      }
    } catch (error) {
      console.error('Error saving artist:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddTag = () => {
    setTags([...tags, ''])
  }

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index))
  }

  const handleTagChange = (index: number, value: string) => {
    const newTags = [...tags]
    newTags[index] = value
    setTags(newTags)
  }

  const gradientOptions = [
    { value: 'from-blue-500/20 to-purple-500/20', label: 'Blue to Purple', preview: 'bg-gradient-to-r from-blue-500 to-purple-500' },
    { value: 'from-pink-500/20 to-red-500/20', label: 'Pink to Red', preview: 'bg-gradient-to-r from-pink-500 to-red-500' },
    { value: 'from-green-500/20 to-teal-500/20', label: 'Green to Teal', preview: 'bg-gradient-to-r from-green-500 to-teal-500' },
    { value: 'from-yellow-500/20 to-orange-500/20', label: 'Yellow to Orange', preview: 'bg-gradient-to-r from-yellow-500 to-orange-500' },
    { value: 'from-indigo-500/20 to-blue-500/20', label: 'Indigo to Blue', preview: 'bg-gradient-to-r from-indigo-500 to-blue-500' },
    { value: 'from-purple-500/20 to-pink-500/20', label: 'Purple to Pink', preview: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  ]

  if (mode === 'edit' && artistLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-neutral-600">Loading artist...</span>
      </div>
    )
  }

  if (mode === 'edit' && !artistLoading && !artist) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-neutral-800 mb-4">Artist Not Found</h2>
        <p className="text-neutral-600 mb-6">The artist you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/admin/artists')}
          className="btn-primary"
        >
          Back to Artists
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 relative">
      {/* Background Gradient Flares */}
      <div className="absolute top-0 right-1/3 w-[600px] h-[600px] bg-gradient-to-bl from-blue-200/80 to-purple-200/80 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      <div className="absolute bottom-40 left-1/4 w-[500px] h-[500px] bg-gradient-to-tr from-purple-200/65 to-blue-200/65 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4">
        <button
          onClick={() => navigate('/admin/artists')}
          className="p-2 hover:bg-neutral-100 rounded-lg transition-colors flex-shrink-0"
        >
          <FiArrowLeft size={20} className="text-neutral-600" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900">
            {mode === 'add' ? 'Add New Artist' : `Edit ${artist?.name}`}
          </h1>
          <p className="text-neutral-600 text-sm">
            {mode === 'add' ? 'Add a new musician to your roster' : 'Update artist information'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="p-[1px] bg-gradient-to-r from-blue-500/50 to-purple-500/50 rounded-lg">
          <div className="bg-white rounded-lg p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-5 flex items-center space-x-2">
            <FiUser size={18} />
            <span>Basic Information</span>
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Artist Name *
              </label>
              <input
                {...register('name', { required: 'Artist name is required' })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="e.g., Luna Rodriguez"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Genre *
              </label>
              <input
                {...register('genre', { required: 'Genre is required' })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="e.g., Indie Pop"
              />
              {errors.genre && (
                <p className="mt-1 text-sm text-red-600">{errors.genre.message}</p>
              )}
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Description *
              </label>
              <textarea
                {...register('description', { required: 'Description is required' })}
                rows={4}
                className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-vertical"
                placeholder="Brief description of the artist's style and background..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Profile Image *
              </label>
              
              <div className="space-y-3">
                {/* Image Preview */}
                {watch('image') && (
                  <div className="w-32 h-32 rounded-xl overflow-hidden border border-neutral-200">
                    <img
                      src={watch('image')}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* URL Input with Browse Button */}
                <div className="flex space-x-3">
                  <input
                    {...register('image', { required: 'Image is required' })}
                    className="flex-1 px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                    placeholder="https://example.com/artist-photo.jpg"
                  />
                  <button
                    type="button"
                    onClick={() => setShowMediaPicker(true)}
                    className="px-4 py-2.5 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors flex items-center space-x-2 text-sm font-medium"
                  >
                    <FiImage size={16} />
                    <span>Browse</span>
                  </button>
                </div>
                
                {errors.image && (
                  <p className="mt-1 text-sm text-red-600">{errors.image.message}</p>
                )}
                
                <p className="text-sm text-neutral-600">
                  Click "Browse" to select from uploaded images or paste an external URL
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Video Section */}
        <div className="p-[1px] bg-gradient-to-r from-purple-500/50 to-pink-500/50 rounded-lg">
          <div className="bg-white rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center">
              <FiVideo className="mr-2 text-purple-600" />
              Performance Video
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Video URL
                </label>
                
                <div className="space-y-3">
                  {/* Video Preview */}
                  {watch('videoUrl') && (
                    <div className="w-full aspect-video rounded-xl overflow-hidden border border-neutral-200">
                      <video
                        src={watch('videoUrl')}
                        className="w-full h-full object-cover"
                        controls
                        muted
                        preload="metadata"
                      />
                    </div>
                  )}

                  {/* URL Input with Browse Button */}
                  <div className="flex space-x-3">
                    <input
                      {...register('videoUrl')}
                      className="flex-1 px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                      placeholder="https://example.com/artist-video.mp4"
                    />
                    <button
                      type="button"
                      onClick={() => setShowVideoPicker(true)}
                      className="px-4 py-2.5 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors flex items-center space-x-2 text-sm font-medium"
                    >
                      <FiVideo size={16} />
                      <span>Browse</span>
                    </button>
                  </div>
                  
                  <p className="text-sm text-neutral-600">
                    Optional: Upload a performance video to showcase the artist
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Video Thumbnail URL
                </label>
                
                <div className="space-y-3">
                  {/* Thumbnail Preview */}
                  {watch('videoThumbnail') && (
                    <div className="w-32 h-20 rounded-xl overflow-hidden border border-neutral-200">
                      <img
                        src={watch('videoThumbnail')}
                        alt="Video Thumbnail"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <input
                    {...register('videoThumbnail')}
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                    placeholder="https://example.com/video-thumbnail.jpg"
                  />
                  
                  <p className="text-sm text-neutral-600">
                    Optional: Custom thumbnail for the video (will auto-generate if not provided)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="p-[1px] bg-gradient-to-r from-indigo-500/50 to-blue-500/50 rounded-lg">
          <div className="bg-white rounded-lg p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-5 flex items-center space-x-2">
            <FiLink size={18} />
            <span>Social Links</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Instagram
              </label>
              <input
                {...register('socialLinks.instagram')}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="https://instagram.com/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Spotify
              </label>
              <input
                {...register('socialLinks.spotify')}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="https://open.spotify.com/artist/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                YouTube
              </label>
              <input
                {...register('socialLinks.youtube')}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="https://youtube.com/channel/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Website
              </label>
              <input
                {...register('socialLinks.website')}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="https://artistwebsite.com"
              />
            </div>
          </div>
          </div>
        </div>

        {/* Settings & Tags */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-5 flex items-center space-x-2">
            <FiTag size={18} />
            <span>Settings & Tags</span>
          </h2>

          <div className="space-y-6">
            {/* Status Toggles */}
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  {...register('featured')}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <div className="flex items-center space-x-2">
                  <FiStar size={16} className="text-yellow-500" />
                  <span className="text-sm font-medium text-neutral-700">Featured Artist</span>
                </div>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  {...register('active')}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <div className="flex items-center space-x-2">
                  <FiEye size={16} className="text-green-500" />
                  <span className="text-sm font-medium text-neutral-700">Active (Visible on site)</span>
                </div>
              </label>
            </div>

            {/* Gradient Selection */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-3">
                Card Gradient
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {gradientOptions.map((option) => (
                  <label key={option.value} className="cursor-pointer">
                    <input
                      type="radio"
                      {...register('gradient')}
                      value={option.value}
                      className="sr-only"
                    />
                    <div className={`p-3 border-2 rounded-lg transition-all ${
                      watch('gradient') === option.value 
                        ? 'border-blue-600 bg-blue-50' 
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}>
                      <div className={`h-8 rounded-lg mb-2 ${option.preview}`}></div>
                      <p className="text-xs text-neutral-600 text-center">{option.label}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-neutral-700">
                  Tags (for filtering)
                </label>
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="text-sm text-blue-600 hover:text-purple-600 transition-colors flex items-center space-x-1"
                >
                  <FiPlus size={14} />
                  <span>Add Tag</span>
                </button>
              </div>
              
              <div className="space-y-3">
                {tags.map((tag, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      value={tag}
                      onChange={(e) => handleTagChange(index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                      placeholder="e.g., acoustic, wedding, jazz"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(index)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/artists')}
            className="px-5 py-2.5 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors text-sm font-medium w-full sm:w-auto"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={`flex items-center justify-center space-x-2 px-5 py-2.5 rounded-lg transition-all text-sm font-medium w-full sm:w-auto ${
              isSubmitting
                ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-md'
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="loading-spinner w-4 h-4"></div>
                <span>{mode === 'add' ? 'Creating...' : 'Updating...'}</span>
              </>
            ) : (
              <>
                <FiSave size={16} />
                <span>{mode === 'add' ? 'Create Artist' : 'Update Artist'}</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Media Picker Modal */}
      <MediaPicker
        isOpen={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={(imageUrl) => {
          setValue('image', imageUrl)
          setShowMediaPicker(false)
        }}
        selectedUrl={watch('image')}
      />

      {/* Video Picker Modal */}
      <VideoPicker
        isOpen={showVideoPicker}
        onClose={() => setShowVideoPicker(false)}
        onSelect={(videoUrl) => {
          setValue('videoUrl', videoUrl)
          setShowVideoPicker(false)
        }}
        selectedUrl={watch('videoUrl')}
      />
    </div>
  )
}

export default ArtistForm
